<template>
  <div v-if="loading" class="mpf-payment-view__loading">Loading…</div>

  <template v-else-if="payment">
    <MpfCard v-if="payment.state === 'already-paid'" heading="Already paid">
      <p>This invoice has already been paid.</p>
    </MpfCard>

    <MpfCard v-else-if="payment.state === 'cancelled'" heading="Invoice cancelled">
      <p>This invoice has been cancelled and is no longer payable.</p>
    </MpfCard>

    <MpfCard v-else-if="payment.state === 'unavailable'" heading="Payment unavailable">
      <p>
        Online payment is not currently available. Please contact
        {{ practiceName }} to arrange payment.
      </p>
    </MpfCard>

    <template v-else>
      <MpfCard primary heading="Amount due" class="mpf-u-margin-bottom-6">
        <p class="mpf-payment-view__amount">{{ formattedAmountDue }}</p>
      </MpfCard>

      <section v-if="payment.isNonProductionEnvironment" class="mpf-payment-view__test-mode-note">
        <MpfTag text="Test Mode" colour="blue" />
        <p class="mpf-u-margin-top-2">
          This environment runs Stripe in test mode. Use card number 4242 4242 4242 4242, any future
          expiry date, any CVC, and any postcode to complete payment.
        </p>
      </section>

      <div ref="stripeContainer" class="mpf-payment-view__stripe"></div>
    </template>
  </template>
</template>

<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue';
  import { useRoute } from 'vue-router';
  import MpfCard from '../components/MpfCard/MpfCard.vue';
  import MpfTag from '../components/MpfTag/MpfTag.vue';
  import { useTenantStore } from '@/stores/useTenantStore';
  import { fetchInvoicePaymentApi } from '@/services/payment.service';
  import { loadStripeJs, getStripe } from '../stripe/loadStripeJs';
  import { InvoicePayment } from '@/types/payment';

  const route = useRoute();
  const tenantStore = useTenantStore();

  const invoiceId = route.params.invoiceId as string;

  const loading = ref(true);
  const payment = ref<InvoicePayment | null>(null);
  const stripeContainer = ref<HTMLElement | null>(null);

  const practiceName = computed(() => tenantStore.tenant?.localOrganisation?.name);

  const formattedAmountDue = computed(() => {
    if (!payment.value) return '';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(
      payment.value.amountDue / 100,
    );
  });

  async function mountStripeCheckout() {
    if (!payment.value?.clientSecret || !payment.value?.publishableKey || !stripeContainer.value) {
      return;
    }

    await loadStripeJs();

    const stripeOptions = payment.value.processorAccountId
      ? { stripeAccount: payment.value.processorAccountId }
      : {};

    const stripe = getStripe()(payment.value.publishableKey, stripeOptions);
    const checkout = await stripe.initEmbeddedCheckout({
      clientSecret: payment.value.clientSecret,
    });

    checkout.mount(stripeContainer.value);
  }

  onMounted(async () => {
    try {
      payment.value = await fetchInvoicePaymentApi(invoiceId);
    } finally {
      loading.value = false;
    }

    if (payment.value?.state === 'payable') {
      await nextTick(); // let the v-else branch render the mount target div first
      await mountStripeCheckout();
    }
  });
</script>

<style scoped lang="scss">
  .mpf-payment-view__amount {
    font-size: 2.5rem;
    font-weight: 700;
  }

  .mpf-payment-view__stripe {
    min-height: 30rem;
  }
</style>
